import { collectStringLeaves } from './translation-json';

const WORD_RE = /\S+/g;

function normalizeForCompare(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenizeWords(text) {
  const normalized = normalizeForCompare(text);
  return normalized.match(WORD_RE) || [];
}

/**
 * Longest common subsequence length on word tokens (same approach as compare highlights).
 * @param {string[]} a
 * @param {string[]} b
 * @returns {number}
 */
function countMatchingWords(a, b) {
  const n = a.length;
  const m = b.length;
  if (!n || !m) return 0;

  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp[0][0];
}

function referenceWordStats(referenceValue, machineValue) {
  const referenceWords = tokenizeWords(referenceValue);
  const machineWords = tokenizeWords(machineValue);
  const total = referenceWords.length;

  if (!total) {
    return { matched: 0, total: 0, exact: machineWords.length === 0 };
  }

  const matched = countMatchingWords(referenceWords, machineWords);
  return {
    matched,
    total,
    exact: matched === total && machineWords.length === total
  };
}

function getLeafMap(obj) {
  const map = new Map();
  for (const entry of collectStringLeaves(obj)) {
    map.set(entry.path, entry.value);
  }
  return map;
}

/**
 * Score how well the machine English preview matches the stored en.json master file.
 * Reference-first: percent is matched reference words / total reference words.
 *
 * @param {*} machinePreview AI English output (locale → English), same JSON shape as the locale file
 * @param {*} referenceEnglish Loaded en.json from storage (ground truth)
 * @returns {{
 *   percent: number,
 *   matched: number,
 *   compared: number,
 *   referenceTotal: number,
 *   matchedWords: number,
 *   totalWords: number,
 *   mismatched: number,
 *   missingInMachine: number,
 *   extraInMachine: number,
 *   missingInReference: number,
 *   onlyInReference: number
 * } | null}
 */
export function scoreEnglishComparison(machinePreview, referenceEnglish) {
  if (
    machinePreview === null ||
    machinePreview === undefined ||
    referenceEnglish === null ||
    referenceEnglish === undefined
  ) {
    return null;
  }

  if (typeof machinePreview === 'string' || typeof referenceEnglish === 'string') {
    return null;
  }

  const machineMap = getLeafMap(machinePreview);
  const referenceMap = getLeafMap(referenceEnglish);

  if (!referenceMap.size && !machineMap.size) {
    return {
      percent: 100,
      matched: 0,
      compared: 0,
      referenceTotal: 0,
      matchedWords: 0,
      totalWords: 0,
      mismatched: 0,
      missingInMachine: 0,
      extraInMachine: 0,
      missingInReference: 0,
      onlyInReference: 0
    };
  }

  let mismatched = 0;
  let missingInMachine = 0;
  let matchedWords = 0;
  let totalWords = 0;

  for (const [path, referenceValue] of referenceMap.entries()) {
    const referenceWords = tokenizeWords(referenceValue);
    totalWords += referenceWords.length;

    if (!machineMap.has(path)) {
      missingInMachine += 1;
      continue;
    }

    const stats = referenceWordStats(referenceValue, machineMap.get(path));
    matchedWords += stats.matched;

    if (!stats.exact) {
      mismatched += 1;
    }
  }

  let extraInMachine = 0;
  for (const path of machineMap.keys()) {
    if (!referenceMap.has(path)) {
      extraInMachine += 1;
    }
  }

  const percent =
    totalWords > 0 ? Math.round((matchedWords / totalWords) * 1000) / 10 : 0;

  return {
    percent,
    matched: matchedWords,
    compared: totalWords,
    referenceTotal: totalWords,
    matchedWords,
    totalWords,
    mismatched,
    missingInMachine,
    extraInMachine,
    missingInReference: extraInMachine,
    onlyInReference: missingInMachine
  };
}

/** Traffic-light accuracy bands (word-match % vs en.json). */
export const ACCURACY_GREEN_MIN = 90;
export const ACCURACY_AMBER_MIN = 70;

/**
 * @param {number} percent
 * @returns {'green'|'amber'|'red'|'none'}
 */
export function accuracyLevel(percent) {
  const value = Number(percent);
  if (!Number.isFinite(value) || value <= 0) return 'none';
  if (value >= ACCURACY_GREEN_MIN) return 'green';
  if (value >= ACCURACY_AMBER_MIN) return 'amber';
  return 'red';
}

/**
 * @param {'green'|'amber'|'red'|'none'} level
 * @returns {string}
 */
export function accuracyLevelLabel(level) {
  switch (level) {
    case 'green':
      return 'Green';
    case 'amber':
      return 'Amber';
    case 'red':
      return 'Red';
    default:
      return '';
  }
}

/**
 * Paths and string pairs to highlight in compare panes.
 * @returns {{
 *   mismatched: Map<string, { machine: string, reference: string }>,
 *   missingInMachine: Set<string>,
 *   extraInMachine: Set<string>
 * } | null}
 */
export function buildEnglishComparisonDiff(machinePreview, referenceEnglish) {
  if (
    machinePreview === null ||
    machinePreview === undefined ||
    referenceEnglish === null ||
    referenceEnglish === undefined
  ) {
    return null;
  }

  if (typeof machinePreview === 'string' || typeof referenceEnglish === 'string') {
    return null;
  }

  const machineMap = getLeafMap(machinePreview);
  const referenceMap = getLeafMap(referenceEnglish);
  const mismatched = new Map();
  const missingInMachine = new Set();
  const extraInMachine = new Set();

  for (const [path, referenceValue] of referenceMap.entries()) {
    if (!machineMap.has(path)) {
      missingInMachine.add(path);
      continue;
    }
    const machineValue = machineMap.get(path);
    const stats = referenceWordStats(referenceValue, machineValue);
    if (!stats.exact) {
      mismatched.set(path, {
        machine: String(machineValue ?? ''),
        reference: String(referenceValue ?? '')
      });
    }
  }

  for (const path of machineMap.keys()) {
    if (!referenceMap.has(path)) {
      extraInMachine.add(path);
    }
  }

  return { mismatched, missingInMachine, extraInMachine };
}
